import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import {
  fetchDecisionReceiptsForCandidateKeys,
  RECEIPT_ACTIONS,
} from '../reviewableItems/reviewableDecisionReceipts';
import { buildReviewableCandidatesFromCareSource } from './careSourceReviewableCandidates';
import {
  formatDestinationCount,
  sortDestinationEntries,
} from './profileReviewQueueModel';
import {
  CAPTURE_DESTINATIONS,
  getCaptureDestinationMessage,
} from './captureDestinationPolicy';

const HANDLED_ACTIONS = new Set([
  RECEIPT_ACTIONS.RESOLVED,
  RECEIPT_ACTIONS.IGNORED,
  RECEIPT_ACTIONS.AUTO_APPLIED,
]);

const DestinationBreakdown = ({ title, destinations = {} }) => {
  const entries = sortDestinationEntries(destinations);
  if (!entries.length) return null;
  return (
    <Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, color: colors.semantic.ink }}>
        {title}
      </Typography>
      <Stack component="ul" spacing={0.2} sx={{ mt: 0.25, mb: 0, pl: 2.3 }}>
        {entries.map(([label, count]) => (
          <Typography component="li" key={label} sx={{ fontSize: '0.78rem', color: colors.semantic.ink2 }}>
            {`${label} (${count})`}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

const ExtractionResultSummary = ({ source = {}, summary, onReview }) => {
  const candidates = React.useMemo(
    () => buildReviewableCandidatesFromCareSource(source),
    [source]
  );
  const candidateKeys = React.useMemo(
    () => candidates.map((candidate) => candidate.key).filter(Boolean),
    [candidates]
  );
  const [receiptState, setReceiptState] = React.useState({
    loading: candidateKeys.length > 0,
    reviewed: 0,
    skipped: 0,
    savedDestinations: {},
    handledDestinations: {},
  });

  React.useEffect(() => {
    let active = true;
    if (!candidateKeys.length) {
      setReceiptState({
        loading: false,
        reviewed: 0,
        skipped: 0,
        savedDestinations: {},
        handledDestinations: {},
      });
      return () => {
        active = false;
      };
    }

    setReceiptState((current) => ({ ...current, loading: true }));
    fetchDecisionReceiptsForCandidateKeys(candidateKeys)
      .then((receipts) => {
        if (!active) return;
        const handled = (receipts || []).filter((receipt) => HANDLED_ACTIONS.has(receipt.action));
        const candidatesByKey = new Map(candidates.map((candidate) => [candidate.key, candidate]));
        const savedDestinations = handled.reduce((counts, receipt) => {
          if (receipt.action === RECEIPT_ACTIONS.IGNORED) return counts;
          const candidate = candidatesByKey.get(receipt.candidateKey || receipt.id);
          const label = candidate?.domain === 'goal' ? 'Goals' : 'Allergies';
          return { ...counts, [label]: (counts[label] || 0) + 1 };
        }, {});
        const handledDestinations = handled.reduce((counts, receipt) => {
          const candidate = candidatesByKey.get(receipt.candidateKey || receipt.id);
          const label = candidate?.domain === 'goal' ? 'Goals' : 'Allergies';
          return { ...counts, [label]: (counts[label] || 0) + 1 };
        }, {});
        setReceiptState({
          loading: false,
          reviewed: handled.length,
          skipped: handled.filter((receipt) => receipt.action === RECEIPT_ACTIONS.IGNORED).length,
          savedDestinations,
          handledDestinations,
        });
      })
      .catch(() => {
        if (active) setReceiptState({
          loading: false,
          reviewed: 0,
          skipped: 0,
          savedDestinations: {},
          handledDestinations: {},
        });
      });

    return () => {
      active = false;
    };
  }, [candidateKeys, candidates]);

  if (!summary?.total) return null;
  const medicationSummary = source.medicationReviewSummary || {};
  const handledMedicationCount = Number(medicationSummary.resolvedCount || 0)
    + Number(medicationSummary.ignoredCount || 0)
    + Number(medicationSummary.notActionableCount || 0);
  const reviewed = Math.min(
    summary.reviewTotal,
    summary.reviewed + receiptState.reviewed + handledMedicationCount
  );
  const waitingForReview = Math.max(0, summary.reviewTotal - reviewed);
  const skipped = summary.skipped
    + receiptState.skipped
    + Number(medicationSummary.ignoredCount || 0);
  const savedAfterReview = Math.max(0, reviewed - skipped);
  const reviewComplete = summary.reviewTotal > 0 && waitingForReview === 0;
  const everythingProcessed = waitingForReview === 0
    && summary.automaticSaving === 0
    && !receiptState.loading;
  const savedDestinations = Object.entries(receiptState.savedDestinations)
    .reduce((counts, [label, count]) => ({
      ...counts,
      [label]: (counts[label] || 0) + count,
    }), { ...(summary.savedDestinations || {}) });
  if (Number(medicationSummary.resolvedCount || 0) > 0) {
    savedDestinations.Medications = Number(medicationSummary.resolvedCount);
  }
  const displayedPendingDestinations = Object.entries({
    ...(summary.pendingDestinations || summary.destinations),
  }).reduce((counts, [label, count]) => {
    const handledCandidateCount = receiptState.handledDestinations[label] || 0;
    const handledMedicationDestinationCount = label === 'Medications'
      ? handledMedicationCount
      : 0;
    const pendingCount = Math.max(
      0,
      count - handledCandidateCount - handledMedicationDestinationCount
    );
    if (pendingCount) counts[label] = pendingCount;
    return counts;
  }, {});

  return (
    <Box
      aria-label="Extraction result summary"
      sx={{
        p: 1.25,
        borderRadius: '16px',
        border: `1px solid ${alpha(colors.semantic.sageDeep, 0.22)}`,
        bgcolor: alpha(colors.semantic.sageMist, 0.45),
      }}
    >
      <Stack spacing={0.65}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, color: alpha(colors.semantic.ink, 0.62) }}>
          Results from this update
        </Typography>
        <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: colors.semantic.ink }}>
          {everythingProcessed
            ? (reviewComplete ? 'Review complete' : 'Everything is complete')
            : `${summary.total} useful detail${summary.total === 1 ? '' : 's'} found`}
        </Typography>
        {!everythingProcessed ? (
          <Typography sx={{ fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.62), lineHeight: 1.4 }}>
            You added a note. Here is what happened next.
          </Typography>
        ) : null}

        {summary.automaticSaved > 0 ? (
          <Stack direction="row" spacing={0.55} alignItems="center">
            <CheckCircleRoundedIcon sx={{ fontSize: 17, color: colors.semantic.sageDeep }} />
            <Typography sx={{ fontSize: '0.84rem', color: colors.semantic.ink2 }}>
              {getCaptureDestinationMessage(
                CAPTURE_DESTINATIONS.CARE_KNOWLEDGE,
                summary.automaticSaved
              )}
            </Typography>
          </Stack>
        ) : null}
        {summary.automaticSaving > 0 ? (
          <Typography sx={{ fontSize: '0.84rem', color: colors.semantic.ink2 }}>
            {`${summary.automaticSaving} observation${summary.automaticSaving === 1 ? '' : 's'} found → Adding to Care Knowledge.`}
          </Typography>
        ) : null}
        {summary.alreadyKnown > 0 ? (
          <Typography sx={{ fontSize: '0.84rem', color: colors.semantic.ink2 }}>
            {`${summary.alreadyKnown} already known — no duplicate was added`}
          </Typography>
        ) : null}
        {reviewComplete && savedAfterReview > 0 ? sortDestinationEntries(savedDestinations).map(([label, count]) => (
          <Stack key={label} direction="row" spacing={0.55} alignItems="center">
            <CheckCircleRoundedIcon sx={{ fontSize: 17, color: colors.semantic.sageDeep }} />
            <Typography sx={{ fontSize: '0.84rem', color: colors.semantic.ink2 }}>
              {`${formatDestinationCount(label, count)} added`}
            </Typography>
          </Stack>
        )) : null}
        {receiptState.loading ? (
          <Typography sx={{ fontSize: '0.84rem', color: colors.semantic.ink2 }}>
            Checking review status…
          </Typography>
        ) : waitingForReview > 0 ? (
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 750, color: colors.semantic.ink }}>
            {`${waitingForReview} need${waitingForReview === 1 ? 's' : ''} your review`}
          </Typography>
        ) : null}
        {skipped > 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: colors.semantic.ink2 }}>
            {`${skipped} skipped`}
          </Typography>
        ) : null}
        {summary.unableToClassify > 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: colors.semantic.ink2 }}>
            {`${summary.unableToClassify} could not be classified`}
          </Typography>
        ) : null}
        {summary.timelineOnly > 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: colors.semantic.ink2 }}>
            {`${summary.timelineOnly} kept with this Timeline update`}
          </Typography>
        ) : null}
        {!everythingProcessed ? (
          <Stack spacing={0.65} sx={{ pt: 0.2 }}>
            {summary.automaticSaved > 0 ? (
              <DestinationBreakdown
                title="Added to Care Knowledge"
                destinations={{ Observations: summary.automaticSaved }}
              />
            ) : null}
            {waitingForReview > 0 ? (
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, color: colors.semantic.ink }}>
                  Needs your review
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                  {sortDestinationEntries(displayedPendingDestinations).map(([label, count]) => (
                    <Typography key={label} sx={{ fontSize: '0.78rem', color: colors.semantic.ink2 }}>
                      {getCaptureDestinationMessage(label, count)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        ) : null}
        {everythingProcessed ? (
          <Typography sx={{ pt: 0.2, fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.72), lineHeight: 1.4 }}>
            Everything from this update has now been processed.
          </Typography>
        ) : null}
        {!receiptState.loading && waitingForReview > 0 ? (
          <Button
            variant="contained"
            size="small"
            onClick={onReview}
            sx={{ alignSelf: 'flex-start', mt: 0.25, textTransform: 'none', fontWeight: 850, boxShadow: 'none' }}
          >
            {`Review ${waitingForReview} item${waitingForReview === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ExtractionResultSummary;
